import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// File status enum
export const FILE_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error'
};

const UploadContext = createContext(null);

export function UploadProvider({ children }) {
  const [uploads, setUploads] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const pauseFlags = useRef({}); // Track pause state per upload

  // Warn user before leaving page during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = 'Upload in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  // Upload a single chunk with retry logic - uses streaming endpoint (no base64 overhead)
  const uploadChunkWithRetry = async (chunkBlob, chunkIndex, totalChunks, uploadId, brandId, fileType, mode, filename, fileSize, maxRetries = 5) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Create FormData for binary upload (more efficient than base64)
        const formData = new FormData();
        formData.append('file', chunkBlob, `chunk_${chunkIndex}`);
        
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout
        
        // Build URL with query params
        const params = new URLSearchParams({
          chunk_index: chunkIndex.toString(),
          total_chunks: totalChunks.toString(),
          upload_id: uploadId || '',
          file_type: fileType,
          mode: mode,
          filename: filename,
          file_size: fileSize.toString()
        });
        
        const response = await fetch(`${API}/api/settings/csv-upload-stream/${brandId}?${params}`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.status === 401) {
          // Session expired - wait and retry
          console.warn(`Chunk ${chunkIndex}: Auth error, retrying (attempt ${attempt}/${maxRetries})`);
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 2000 * attempt));
            continue;
          }
          throw new Error('Session expired. Please refresh and try again.');
        }
        
        if (!response.ok) {
          console.warn(`Chunk ${chunkIndex}: HTTP ${response.status}, retrying (attempt ${attempt}/${maxRetries})`);
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 2000 * attempt));
            continue;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        if (!result.success && result.error) {
          console.warn(`Chunk ${chunkIndex}: Server error - ${result.error}`);
          
          // If missing chunks error, we can retry those specific chunks
          if (result.can_retry && result.missing_chunks) {
            console.log(`Missing chunks detected: ${result.missing_chunks}`);
          }
          
          if (attempt < maxRetries && !result.error.includes('corrupted')) {
            await new Promise(r => setTimeout(r, 2000 * attempt));
            continue;
          }
          throw new Error(result.error);
        }
        
        return result;
      } catch (error) {
        console.error(`Chunk ${chunkIndex} upload error (attempt ${attempt}/${maxRetries}):`, error.message);
        
        if (error.name === 'AbortError') {
          console.warn(`Chunk ${chunkIndex}: Request timed out`);
        }
        
        if (attempt === maxRetries) {
          throw error;
        }
        // Exponential backoff with jitter
        const backoff = Math.min(2000 * Math.pow(2, attempt - 1) + Math.random() * 1000, 30000);
        await new Promise(r => setTimeout(r, backoff));
      }
    }
  };

  // Update upload session on server
  const updateServerSession = async (sessionId, data) => {
    try {
      await fetch(`${API}/api/settings/upload-session/${sessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (e) {
      console.error('Failed to update session:', e);
    }
  };

  // Create upload session on server
  const createServerSession = async (brandId, filename, totalChunks, fileSize) => {
    try {
      const response = await fetch(`${API}/api/settings/upload-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: brandId, filename, total_chunks: totalChunks, file_size: fileSize })
      });
      const data = await response.json();
      return data.session_id;
    } catch (e) {
      console.error('Failed to create session:', e);
      return null;
    }
  };

  // Start uploading a file - uses streaming binary upload (no base64 overhead)
  const startUpload = useCallback(async (file, brandId, fileType = 'sales', mode = 'append', resumeFrom = 0, existingSessionId = null) => {
    const uploadId = existingSessionId || `${file.name}-${Date.now()}`;
    
    // Use 1MB chunks for better reliability on slow networks
    const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Create server session
    const serverSessionId = existingSessionId || await createServerSession(brandId, file.name, totalChunks, file.size);
    
    pauseFlags.current[uploadId] = false;
    
    setUploads(prev => ({
      ...prev,
      [uploadId]: {
        id: uploadId,
        serverSessionId,
        file,
        fileName: file.name,
        fileSize: file.size,
        brandId,
        fileType,
        mode,
        status: FILE_STATUS.UPLOADING,
        progress: resumeFrom > 0 ? Math.round((resumeFrom / totalChunks) * 100) : 0,
        chunksTotal: totalChunks,
        chunksUploaded: resumeFrom,
        result: null,
        error: null,
        startedAt: new Date().toISOString()
      }
    }));
    
    setIsUploading(true);

    let serverUploadId = '';

    try {
      for (let chunkIndex = resumeFrom; chunkIndex < totalChunks; chunkIndex++) {
        // Check if paused
        if (pauseFlags.current[uploadId]) {
          setUploads(prev => ({
            ...prev,
            [uploadId]: { 
              ...prev[uploadId], 
              status: FILE_STATUS.PAUSED,
              chunksUploaded: chunkIndex
            }
          }));
          await updateServerSession(serverSessionId, { status: 'paused', chunks_uploaded: chunkIndex });
          setIsUploading(false);
          toast.info(`${file.name}: Upload paused at ${Math.round((chunkIndex / totalChunks) * 100)}%`);
          return uploadId;
        }

        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);
        
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        
        setUploads(prev => ({
          ...prev,
          [uploadId]: { 
            ...prev[uploadId], 
            progress,
            chunksUploaded: chunkIndex + 1,
            status: FILE_STATUS.UPLOADING
          }
        }));

        // Use streaming upload (binary, no base64)
        const result = await uploadChunkWithRetry(
          chunkBlob,
          chunkIndex,
          totalChunks,
          serverUploadId,
          brandId,
          fileType,
          mode,
          file.name,
          file.size
        );

        if (chunkIndex === 0 && result.upload_id) {
          serverUploadId = result.upload_id;
        }

        // Update server session periodically
        if (chunkIndex % 10 === 0) {
          await updateServerSession(serverSessionId, { chunks_uploaded: chunkIndex + 1 });
        }

        // Last chunk - processing complete
        if (chunkIndex === totalChunks - 1) {
          const processedCount = result.processed || result.rows_processed || 0;
          
          setUploads(prev => ({
            ...prev,
            [uploadId]: {
              ...prev[uploadId],
              status: FILE_STATUS.COMPLETED,
              progress: 100,
              result: result,
              completedAt: new Date().toISOString()
            }
          }));
          
          // Delete server session on success
          await fetch(`${API}/api/settings/upload-session/${serverSessionId}`, {
            method: 'DELETE',
            credentials: 'include'
          }).catch(() => {});
          
          if (result.job_id) {
            toast.success(`${file.name}: Upload complete! Processing started (Job: ${result.job_id.substring(0, 8)}...)`);
          } else {
            toast.success(`${file.name}: Upload complete! ${processedCount.toLocaleString() || 0} records processed`);
          }
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploads(prev => ({
        ...prev,
        [uploadId]: {
          ...prev[uploadId],
          status: FILE_STATUS.ERROR,
          error: error.message
        }
      }));
      await updateServerSession(serverSessionId, { status: 'error', error: error.message });
      toast.error(`${file.name}: ${error.message}`);
    } finally {
      setIsUploading(false);
      delete pauseFlags.current[uploadId];
    }

    return uploadId;
  }, []);

  // Pause an upload
  const pauseUpload = useCallback((uploadId) => {
    pauseFlags.current[uploadId] = true;
  }, []);

  // Resume an upload
  const resumeUpload = useCallback(async (uploadId) => {
    const upload = uploads[uploadId];
    if (!upload || !upload.file) {
      toast.error('Cannot resume - file data not available');
      return;
    }
    
    pauseFlags.current[uploadId] = false;
    await startUpload(
      upload.file, 
      upload.brandId, 
      upload.fileType, 
      upload.mode, 
      upload.chunksUploaded,
      upload.serverSessionId || uploadId
    );
  }, [uploads, startUpload]);

  // Clear upload from state
  const clearUpload = useCallback((uploadId) => {
    setUploads(prev => {
      const newUploads = { ...prev };
      delete newUploads[uploadId];
      return newUploads;
    });
    delete pauseFlags.current[uploadId];
  }, []);

  // Clear all uploads
  const clearAllUploads = useCallback(() => {
    setUploads({});
    pauseFlags.current = {};
  }, []);

  // Get all uploads as array
  const getAllUploads = useCallback(() => {
    return Object.values(uploads);
  }, [uploads]);

  // Check if any upload is in progress
  const hasActiveUpload = useCallback(() => {
    return Object.values(uploads).some(u => 
      u.status === FILE_STATUS.UPLOADING || u.status === FILE_STATUS.PROCESSING
    );
  }, [uploads]);

  const value = {
    uploads,
    isUploading,
    startUpload,
    pauseUpload,
    resumeUpload,
    clearUpload,
    clearAllUploads,
    getAllUploads,
    hasActiveUpload,
    FILE_STATUS
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUpload() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
}
